import type { Brand, ChatbotConfig, Environment } from "../lib/ChatbotConfig";

// One place decides what an unconfigured widget falls back to. Theme,
// deployment ID and data protection link all derive from the brand, so they
// must never disagree about the default.
export const DEFAULT_BRAND: Brand = "training";
export const DEFAULT_ENVIRONMENT: Environment = "prod";

const brands: readonly Brand[] = ["training", "schools", "group", "academy"];

const environments: readonly Environment[] = ["prod", "stage", "dev"];

// Production hostname fragment → the brand that page has to embed with. This is
// a *canary*, never a source: the config stays authoritative, and a host that is
// not listed here (staging, previews, localhost, landing pages) says nothing
// about the brand and is ignored.
const brandByHostFragment: ReadonlyArray<[string, Brand]> = [
  ["wbstraining", "training"],
  ["wbs-gruppe", "group"],
  ["wbs-schulen", "schools"],
  ["wbsakademie", "academy"],
];

export type ConfigIssueLevel = "error" | "warning";

export interface ConfigIssue {
  level: ConfigIssueLevel;
  scope: string;
  message: string;
  metadata: Record<string, unknown>;
}

export interface ValidatedChatbotConfig {
  /** The config to actually run with — invalid fields replaced by defaults. */
  config: ChatbotConfig;
  /** Everything wrong with the config the host passed in. */
  issues: ConfigIssue[];
}

const isBrand = (value: unknown): value is Brand =>
  brands.includes(value as Brand);

const isEnvironment = (value: unknown): value is Environment =>
  environments.includes(value as Environment);

/**
 * The brand a hostname belongs to, if it is one of the production domains.
 * Only ever a hint: inside the iframe deployment it is the one thing that
 * identifies the embedding page, everywhere else the config wins.
 */
export const brandForHostname = (hostname: string): Brand | undefined =>
  brandByHostFragment.find(([fragment]) =>
    hostname.toLowerCase().includes(fragment),
  )?.[1];

/**
 * Checks the config a host page embedded the widget with, replaces invalid
 * values by the defaults and collects what was wrong. Pure — the caller decides
 * when and how to report the issues.
 *
 * `brand` is only enforced by TypeScript, which the standalone bundle's plain
 * `<script>` consumers (TYPO3) never see. A wrong brand is not cosmetic: it
 * points the widget at another brand's Genesys deployment and shows the wrong
 * data protection statement, so it is reported as an error rather than fixed
 * quietly.
 */
export const validateChatbotConfig = (
  config: ChatbotConfig,
  hostname: string = globalThis.location?.hostname ?? "",
): ValidatedChatbotConfig => {
  const issues: ConfigIssue[] = [];

  const brand = isBrand(config.brand) ? config.brand : DEFAULT_BRAND;

  if (!isBrand(config.brand)) {
    issues.push({
      level: "error",
      scope: "chatbotConfig.brand",
      message:
        config.brand === undefined
          ? `No "brand" in the chatbot config — falling back to "${DEFAULT_BRAND}". Theme, Genesys deployment and data protection link may all be wrong.`
          : `Unknown "brand" in the chatbot config — falling back to "${DEFAULT_BRAND}". Theme, Genesys deployment and data protection link may all be wrong.`,
      metadata: { received: config.brand, expected: brands },
    });
  }

  const environment = isEnvironment(config.environment)
    ? config.environment
    : DEFAULT_ENVIRONMENT;

  if (!isEnvironment(config.environment)) {
    issues.push({
      level: "error",
      scope: "chatbotConfig.environment",
      message:
        config.environment === undefined
          ? `No "environment" in the chatbot config — falling back to "${DEFAULT_ENVIRONMENT}".`
          : `Unknown "environment" in the chatbot config — falling back to "${DEFAULT_ENVIRONMENT}".`,
      metadata: { received: config.environment, expected: environments },
    });
  }

  // Only meaningful once the brand itself is valid — otherwise the mismatch is
  // just an echo of the issue reported above.
  const expectedBrand = brandForHostname(hostname);

  if (isBrand(config.brand) && expectedBrand && expectedBrand !== brand) {
    issues.push({
      level: "error",
      scope: "chatbotConfig.brand",
      message: `The chatbot is embedded with brand "${brand}" on a "${expectedBrand}" domain. The widget keeps using the configured brand — check the embedding if that is not intended.`,
      metadata: { configured: brand, hostname, expected: expectedBrand },
    });
  }

  return {
    config: { ...config, brand, environment },
    issues,
  };
};
